import { queryOptions, useQuery } from '@tanstack/react-query';
import { AttributeChipData } from '../../components/Prediction/types';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { MipVolumeEstimationDTO, TruckRecordingMipService } from '../generated';
import { transformVolumeEstimation } from './volumeEstimation';

interface TruckRecordingMipVolumeEstimationOptionsProps {
  jobsiteId?: string;
  mipReportId?: string;
  enabled?: boolean;
}

export function truckRecordingMipVolumeEstimationOptions({
  jobsiteId,
  mipReportId,
  enabled = true,
}: TruckRecordingMipVolumeEstimationOptionsProps) {
  return queryOptions<AttributeChipData[], ApiError>({
    queryKey: ['truckRecordingMipVolumeEstimation', jobsiteId, mipReportId],
    queryFn: async () => {
      if (!jobsiteId || !mipReportId) {
        throw new Error('jobsiteId and mipReportId are required');
      }

      const response = await TruckRecordingMipService.truckRecordingMipControllerGetVolumeEstimation({
        path: { jobsiteId, mipReportId },
      });

      if (response.data !== undefined) {
        return transformMipVolumeEstimation(response.data);
      }

      throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
    },
    enabled: !!jobsiteId && !!mipReportId && !!enabled,
  });
}

export function useTruckRecordingMipVolumeEstimation(props: TruckRecordingMipVolumeEstimationOptionsProps) {
  return useQuery<AttributeChipData[], ApiError>(truckRecordingMipVolumeEstimationOptions(props));
}

function transformMipVolumeEstimation(resp: MipVolumeEstimationDTO): AttributeChipData[] {
  const emptyVolumes = resp.emptyLineOfSightVolumes
    .map((emptyVol) => emptyVol.data.lineOfSightVolume)
    .filter((vol) => vol !== undefined);
  const emptyVolumeMedian = median(emptyVolumes);

  const volumeEstimation = {
    id: resp.id,
    license_plate: resp.fullLineOfSightVolume.data.licensePlate ?? '',
    empty_volume: emptyVolumeMedian ?? 0,
    batch_volume: resp.fullLineOfSightVolume.data.lineOfSightVolume ?? 0,
  };

  return transformVolumeEstimation(volumeEstimation, {});
}

function median(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sorted = [...values].sort((left, right) => left - right);
  const midpoint = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[midpoint - 1] + sorted[midpoint]) / 2;
  }
  return sorted[midpoint];
}
